export const CLOUD_SYNC_READ_TIMEOUT_CODE = "cloud_sync_read_timeout";
export const DEFAULT_CLOUD_READ_TIMEOUT_MS = 20_000;

export class CloudReadTimeoutError extends Error {
  constructor(timeoutMs = DEFAULT_CLOUD_READ_TIMEOUT_MS) {
    super(CLOUD_SYNC_READ_TIMEOUT_CODE);
    this.name = "CloudReadTimeoutError";
    this.code = CLOUD_SYNC_READ_TIMEOUT_CODE;
    this.status = 408;
    this.timeoutMs = timeoutMs;
  }
}

function normalizedTimeoutMs(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0
    ? Math.max(1, Math.trunc(parsed))
    : DEFAULT_CLOUD_READ_TIMEOUT_MS;
}

function upstreamAbortReason(signal) {
  if (signal?.reason !== undefined) return signal.reason;
  const error = new Error("cloud_sync_read_aborted");
  error.name = "AbortError";
  return error;
}

export function createCloudReadDeadline({ timeoutMs = DEFAULT_CLOUD_READ_TIMEOUT_MS } = {}) {
  const deadlineMs = normalizedTimeoutMs(timeoutMs);

  return Object.freeze({
    timeoutMs: deadlineMs,

    run(read, { signal: upstreamSignal = null } = {}) {
      if (typeof read !== "function") {
        return Promise.reject(new TypeError("cloud_sync_read_required"));
      }

      const controller = new AbortController();
      if (upstreamSignal?.aborted) {
        const reason = upstreamAbortReason(upstreamSignal);
        controller.abort(reason);
        return Promise.reject(reason);
      }

      let timeoutId = null;
      let rejectInterrupted;
      const interrupted = new Promise((_, reject) => {
        rejectInterrupted = reject;
      });

      const interrupt = (reason) => {
        if (controller.signal.aborted) return;
        controller.abort(reason);
        rejectInterrupted(reason);
      };
      const onUpstreamAbort = () => interrupt(upstreamAbortReason(upstreamSignal));

      upstreamSignal?.addEventListener("abort", onUpstreamAbort, { once: true });
      timeoutId = setTimeout(() => {
        interrupt(new CloudReadTimeoutError(deadlineMs));
      }, deadlineMs);

      const readResult = Promise.resolve().then(() => {
        if (controller.signal.aborted) throw controller.signal.reason;
        return read(controller.signal);
      });

      return Promise.race([readResult, interrupted]).finally(() => {
        if (timeoutId !== null) clearTimeout(timeoutId);
        upstreamSignal?.removeEventListener("abort", onUpstreamAbort);
      });
    },
  });
}
